import React, { useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import { arrayMove } from '@dnd-kit/sortable';
import { PropertyType } from '@producer/properties';
import { type ColumnDef } from '@tanstack/react-table';

import { useResponsive } from '../../../hooks/useResponsive';
import { MobileShootingScheduleTable } from './shooting-schedule-table.mobile';
import {
  LocationCell,
  CharactersCell,
  SingleSelectCell,
  SmallTextCell,
  TagCell,
  LongTextCell,
  EstimatedTimeCell,
  StartTimeCell,
  ShotCell,
  MultiSelectCell,
} from '../../stripboard/components/stripboard-cells';
import { StripboardTable } from '../../stripboard/components/stripboard-table';

import type { Stripboard, Strip, Project, ShootingDay } from '../../../app/entities';
import type { Property } from '../../../app/entities/property';
import { useCurrentStep } from '../../../hooks/use-current-step';

type ShootingScheduleProps = {
  stripboard: Stripboard;
  strips: Strip[];
  placeholder?: React.ReactNode;
  properties: Property[];
  options?: {
    showHiddenShots?: boolean;
  };
  shootingDays: ShootingDay[];
  project: Project;
  stripIds: string[];
  sortableStripIds: string[];
  totalShootingDays: number;
  unscheduledStrips: Stripboard['unscheduledStrips'];
  hideUnscheduledTable?: boolean;
  onUpdateStrip?: (
    stripId: string,
    update: {
      data?: {
        text?: string;
        estimatedTime?: number;
        isHidden?: boolean;
      };
    },
  ) => Promise<void>;
  onResizeColumn?: (propertyId: string, value: number) => void;
  onResortStrips?: (stripIds: string[], newPosition: number) => Promise<void>;
  onReorderColumn?: (propertyId: string, oldIndex: number, newIndex: number) => void;
  onAddShotToShootingDay?: (shootingDay: ShootingDay) => void;
  getStripPosition?: (stripIndex: number) => number | undefined;

  /**
   * Set to true id the table should scroll in the screen,
   * false if it should stay inline
   * default = true
   */
  scroll?: boolean;
};

export const ShootingSchedule: React.FC<ShootingScheduleProps> = observer(
  ({
    stripboard,
    strips,
    placeholder,
    properties,
    shootingDays,
    project,
    stripIds,
    sortableStripIds,
    totalShootingDays,
    unscheduledStrips,
    hideUnscheduledTable,
    onResortStrips,
    onUpdateStrip,
    onAddShotToShootingDay,
    getStripPosition,
    scroll,
  }) => {
    const { isDesktop } = useResponsive();
    const step = useCurrentStep();
    const view = step.resource;

    const dayBreakStripIds = useMemo(() => {
      return strips.filter((strip) => strip.type === 'dayBreak').map((strip) => strip._id);
    }, [strips]);

    const handleReorderColumn = (propertyId: string, oldIndex: number, newIndex: number) => {
      const visibleProperties = [];
      const hiddenProperties = [];

      view.properties.forEach((property) => {
        if (property.isVisible) {
          visibleProperties.push(property);
        } else {
          hiddenProperties.push({
            ...property,
            order: undefined,
          });
        }
      });

      const reorderedProperties = arrayMove(visibleProperties, oldIndex, newIndex).map(
        (property, index) => ({
          ...property,
          order: index + 1,
        }),
      );

      view.update({
        properties: reorderedProperties.concat(hiddenProperties),
      });
    };

    const handleResizeColumn = (propertyId: string, columnWidth: number) => {
      view.update({
        properties: view.properties.map((property) => {
          if (property.propertyId !== propertyId) {
            return property;
          }

          return {
            ...property,
            columnWidth,
          };
        }),
      });
    };

    const columns: ColumnDef<any>[] = properties.map((property) => ({
      header: property.name,
      accessorKey: property._id,
      minSize: 64,
      // @ts-ignore
      size: property.columnWidth || 150,
      cell: (props) => {
        if (property.resourceType === 'Location') {
          return <LocationCell {...props} property={property} />;
        }

        if (property.resourceType === 'Character') {
          return <CharactersCell {...props} property={property} />;
        }

        if (property.resourceType === 'Tag') {
          return <TagCell {...props} property={property} />;
        }

        // @ts-ignore
        if (property.type === PropertyType.SingleSelect) {
          return <SingleSelectCell property={property} {...props} />;
        }

        // @ts-ignore
        if (property.type === PropertyType.MultiSelect) {
          return <MultiSelectCell property={property} {...props} />;
        }

        // @ts-ignore
        if (property.type === PropertyType.SmallText) {
          return <SmallTextCell {...props} property={property} />;
        }

        // @ts-ignore
        if (property.type === PropertyType.LongText) {
          return <LongTextCell {...props} property={property} />;
        }

        // @ts-ignore
        if (property.type === PropertyType.Duration) {
          return <EstimatedTimeCell {...props} property={property} />;
        }

        // @ts-ignore
        if (property.type === PropertyType.Time) {
          return <StartTimeCell {...props} property={property} />;
        }

        // @ts-ignore
        if (property.type === PropertyType.Media) {
          return <ShotCell {...props} property={property} />;
        }

        return <SmallTextCell {...props} property={property} />;
      },
    }));

    return isDesktop ? (
      <StripboardTable
        stripboard={stripboard}
        columns={columns}
        onReorderColumn={handleReorderColumn}
        onResizeColumn={handleResizeColumn}
        shootingDays={shootingDays}
        project={project}
        stripIds={stripIds}
        sortableStripIds={sortableStripIds}
        totalShootingDays={totalShootingDays}
        unscheduledStrips={unscheduledStrips}
        hideUnscheduledTable={hideUnscheduledTable}
        onResortStrips={onResortStrips}
        onUpdateStrip={onUpdateStrip}
        onAddShotToShootingDay={onAddShotToShootingDay}
        getStripPosition={getStripPosition}
        dayBreakStripIds={dayBreakStripIds}
        scroll={scroll}
      />
    ) : (
      <MobileShootingScheduleTable
        stripboard={stripboard}
        placeholder={placeholder}
        properties={properties}
        shootingDays={shootingDays}
      />
    );
  },
);
